// Comisiones a Nómina — Taller → Reportes → Comisiones (957).
// % de la mano de obra cobrada de lo entregado en el mes, por técnico; se
// descuenta la comisión de un trabajo que volvió en garantía cubierta, y se
// envía como ajuste de ingreso (o egreso si el neto es negativo) al rol.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, HandCoins, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, money, useSort, SortTh, tallerRpc, QK, useDialogoEnLinea, type Catalogo } from './shared';

interface TecnicoComision {
  tecnico_id: string; tecnico: string; empleado_id: string | null; empleado: string | null; pct: number; base_mo: number;
  ordenes: number; comision: number; reversion: number; reingresos: number; total: number; enviada: { total: number; fecha: string } | null;
}
interface ComisionesResp { periodo: string; tecnicos: TecnicoComision[] }
interface Empleado { id: string; nombre: string; cedula: string }

const periodoAnterior = () => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export function ComisionesTaller() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [periodo, setPeriodo] = useState(periodoAnterior());

  const { data, isLoading, isFetching } = useQuery<ComisionesResp>({
    queryKey: [QK, 'comisiones', periodo], queryFn: () => tallerRpc('comisiones_calcular', { _periodo: periodo }), enabled: /^\d{4}-\d{2}$/.test(periodo),
  });
  const { data: cat } = useQuery<Catalogo>({ queryKey: [QK, 'catalogo'], queryFn: () => tallerRpc('catalogo') });
  const { data: empleados = [] } = useQuery<Empleado[]>({ queryKey: [QK, 'empleados_nomina'], queryFn: () => tallerRpc('empleados_nomina') });

  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });
  const tecnicos = data?.tecnicos ?? [];
  const sort = useSort<TecnicoComision>(tecnicos, (r, k) => (k === 'tecnico' ? r.tecnico : k === 'empleado' ? r.empleado : k === 'pct' ? r.pct : k === 'base' ? r.base_mo
    : k === 'ordenes' ? r.ordenes : k === 'comision' ? r.comision : k === 'reversion' ? r.reversion : k === 'total' ? r.total : null));

  const totales = useMemo(() => (data?.tecnicos ?? []).reduce((a, t) => ({ base: a.base + Number(t.base_mo), comision: a.comision + Number(t.comision), reversion: a.reversion + Number(t.reversion), total: a.total + Number(t.total) }),
    { base: 0, comision: 0, reversion: 0, total: 0 }), [data]);

  const esMesCerrado = useMemo(() => {
    const [a, m] = periodo.split('-').map(Number);
    const inicioMesActual = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    return !isNaN(a) && !isNaN(m) && new Date(a, m - 1, 1) < inicioMesActual;
  }, [periodo]);

  const enviar = useMutation({
    mutationFn: () => tallerRpc<{ enviadas: number; sin_empleado: string[] }>('comisiones_enviar', { _periodo: periodo }),
    onSuccess: (r) => {
      toast.success(`${r.enviadas} comisión(es) enviada(s) a Nómina`);
      if (r.sin_empleado.length) toast.warning(`Sin empleado vinculado: ${r.sin_empleado.join(', ')}`);
      inv();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const tecnicoNomina = useMutation({
    mutationFn: (x: { tecnico: string; empleado: string | null; pct: number }) => tallerRpc('tecnico_nomina', { _tecnico: x.tecnico, _empleado: x.empleado, _pct: x.pct }),
    onSuccess: () => { toast.success('Técnico actualizado'); inv(); void qc.invalidateQueries({ queryKey: [QK, 'catalogo'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const enviarConfirmar = async () => {
    if (!(await dlg.confirmar(`¿Enviar las comisiones de ${periodo} a Nómina?`, {
      detalle: 'Se crea un ajuste (ingreso o egreso) gravable en el rol del período por cada técnico con empleado vinculado y total distinto de cero.',
    }))) return;
    enviar.mutate();
  };

  return (
    <div className="space-y-3 p-4 sm:p-6">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-xl font-semibold"><HandCoins className="h-5 w-5" /> Comisiones de técnicos</h1>
        <div className="flex items-center gap-2">
          <Campo label="Período">
            <input type="month" className={fieldCls} value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
          </Campo>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        La comisión es el % de la mano de obra cobrada de lo entregado en el mes. Se descuenta la comisión de un trabajo que volvió en garantía cubierta
        (reingreso cuyo dictamen fue «cubre»). El total se envía como ajuste de ingreso gravable al rol del período (o egreso, si el neto es negativo).
      </p>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th>
              <SortTh col="tecnico" sort={sort}>Técnico</SortTh>
              <SortTh col="empleado" sort={sort}>Empleado (nómina)</SortTh>
              <SortTh col="pct" sort={sort} className="text-right">%</SortTh>
              <SortTh col="base" sort={sort} className="text-right">Mano de obra</SortTh>
              <SortTh col="ordenes" sort={sort} className="text-right">Órdenes</SortTh>
              <SortTh col="comision" sort={sort} className="text-right">Comisión</SortTh>
              <SortTh col="reversion" sort={sort} className="text-right">Reversión (garantía)</SortTh>
              <SortTh col="total" sort={sort} className="text-right">Total</SortTh>
              <th className="py-2 pr-3">Enviada</th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && sort.sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin técnicos con comisión o mano de obra en el período.</td></tr>}
              {sort.sorted.map((t, i) => (
                <tr key={t.tecnico_id} className="border-b last:border-0">
                  <td className="py-2 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-2 font-medium">{t.tecnico}</td>
                  <td className="py-2 pr-2">
                    {t.empleado ?? <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Sin empleado vinculado</span>}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums">{Number(t.pct)}%</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{money(Number(t.base_mo))}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{t.ordenes}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{money(Number(t.comision))}</td>
                  <td className="py-2 pr-2 text-right tabular-nums text-rose-600">{Number(t.reversion) ? `−${money(Number(t.reversion))}` : '—'}</td>
                  <td className={`py-2 pr-2 text-right font-semibold tabular-nums ${Number(t.total) < 0 ? 'text-rose-600' : ''}`}>{money(Number(t.total))}</td>
                  <td className="py-2 pr-3 text-xs">{t.enviada ? `${money(Number(t.enviada.total))} enviada` : '—'}</td>
                </tr>
              ))}
            </tbody>
            {tecnicos.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
              <td colSpan={4} className="py-2 pl-3">Total</td>
              <td className="py-2 pr-2 text-right tabular-nums">{money(totales.base)}</td>
              <td />
              <td className="py-2 pr-2 text-right tabular-nums">{money(totales.comision)}</td>
              <td className="py-2 pr-2 text-right tabular-nums text-rose-600">{totales.reversion ? `−${money(totales.reversion)}` : '—'}</td>
              <td className="py-2 pr-2 text-right tabular-nums">{money(totales.total)}</td>
              <td />
            </tr></tfoot>}
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button isLoading={enviar.isPending || isFetching} disabled={!esMesCerrado || tecnicos.length === 0} onClick={() => void enviarConfirmar()}>
          <Send className="mr-1 h-4 w-4" /> Enviar a Nómina
        </Button>
      </div>
      {!esMesCerrado && <p className="text-right text-xs text-muted-foreground">Solo se envían meses cerrados (el mes en curso no se puede enviar).</p>}

      <ConfigurarTecnicos cat={cat} comisionTecnicos={tecnicos} empleados={empleados} onGuardar={(x) => tecnicoNomina.mutate(x)} guardando={tecnicoNomina.isPending} />
    </div>
  );
}

function ConfigurarTecnicos({ cat, comisionTecnicos, empleados, onGuardar, guardando }: {
  cat?: Catalogo; comisionTecnicos: TecnicoComision[]; empleados: Empleado[]; onGuardar: (x: { tecnico: string; empleado: string | null; pct: number }) => void; guardando: boolean;
}) {
  const tecnicos = cat?.tecnicos ?? [];
  const [form, setForm] = useState<Record<string, { empleado: string; pct: string }>>({});
  const valor = (id: string) => {
    if (form[id]) return form[id];
    const c = comisionTecnicos.find((x) => x.tecnico_id === id);
    return { empleado: c?.empleado_id ?? '', pct: String(c?.pct ?? 0) };
  };

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="text-sm font-semibold">Configurar técnicos</div>
        <p className="text-xs text-muted-foreground">Vincule cada técnico con su empleado de Nómina y defina el % de comisión sobre la mano de obra cobrada.</p>
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Técnico</th><th className="py-1 pr-2">Empleado</th><th className="py-1 pr-2 text-right">% comisión</th><th /></tr></thead>
          <tbody>
            {tecnicos.map((t, i) => {
              const v = valor(t.id);
              return (
                <tr key={t.id} className="border-b last:border-0">
                  <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-1 pr-2">{t.nombre}</td>
                  <td className="py-1 pr-2">
                    <select className={`${fieldCls} h-8 w-56`} value={v.empleado} onChange={(e) => setForm({ ...form, [t.id]: { ...v, empleado: e.target.value } })}>
                      <option value="">— Sin vincular —</option>
                      {empleados.map((e) => <option key={e.id} value={e.id}>{e.nombre} ({e.cedula})</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-2 text-right"><input type="number" min={0} max={50} step="0.5" className={`${fieldCls} h-8 w-20 text-right`} value={v.pct} onChange={(e) => setForm({ ...form, [t.id]: { ...v, pct: e.target.value } })} /></td>
                  <td className="py-1 text-right">
                    <Button size="sm" variant="outline" isLoading={guardando} onClick={() => onGuardar({ tecnico: t.id, empleado: v.empleado || null, pct: Number(v.pct) || 0 })}><Save className="mr-1 h-3.5 w-3.5" /> Guardar</Button>
                  </td>
                </tr>
              );
            })}
            {tecnicos.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">Sin técnicos registrados (Catálogo → Técnicos).</td></tr>}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
